import React, { useEffect, useState, useCallback, useRef } from "react";
import * as c from "./carouselLarge.module.scss";
import { Carousel } from "@components/carousel";
import classNames from "classnames";
import MeshGradient from "@components/shared/meshGradient";
import { gsap } from "gsap";
import { debounce } from "lodash";
import { useGSAP } from "@gsap/react";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { Text } from "@components/shared/text";
import { renderRichText } from "gatsby-source-contentful/rich-text";
import { ContentfulCta } from "@components/shared/cta";
import VideoModal from "@components/shared/videoModal/videoModal";

const CarouselLarge = ({
  className,
  carouselHeading,
  carouselLabel,
  carouselDescription,
  cta,
  desktopSlides,
  mobileSlides,
  imageLabels,
  isHome,
}) => {
  const [isMobile, setIsMobile] = useState(null);
  const handleResize = useCallback(
    debounce(() => {
      window.matchMedia("(min-width: 575px)").matches ? setIsMobile(false) : setIsMobile(true);
    }, 200),
    []
  );

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleResize]);

  const carouselConfig = {
    draggable: true,
    loop: true,
    slidesToScroll: 1,
    align: "center",
    active: true,
  };

  const container = useRef(null);
  const carouselContainer = useRef();
  const textRef = useRef(null);

  useGSAP(
    (_context, _contextSafe) => {
      let mm = gsap.matchMedia();
      let wrapperWidth = 1200;
      let windowWidth = Math.max(
        (window.innerWidth - wrapperWidth) / 2,
        Math.min(window.innerWidth - wrapperWidth, 400)
      );
      function getBgWidth() {
        wrapperWidth = 1200;
        windowWidth = Math.max((window.innerWidth - wrapperWidth) / 2, Math.min(window.innerWidth - wrapperWidth, 400));
      }
      mm.add("(min-width: 1100px)", () => {
        // Only apply the carousel transition on larger screens

        window.addEventListener("resize", getBgWidth);
        gsap.set(carouselContainer.current, { "--clipPath": windowWidth + "px" });
        gsap
          .timeline({
            paused: false,
            scrollTrigger: {
              trigger: carouselContainer.current,
              start: () => "top 100%+=100px",
              end: () => `bottom 0%-=100px`,
              invalidateOnRefresh: false,
              scrub: 1,
            },
          })
          .to(carouselContainer.current, { "--clipPath": "0px", duration: 35 })
          .to(carouselContainer.current, { "--clipPath": windowWidth / 1.5 + "px", duration: 15 });
      });

      return () => {
        window.removeEventListener("resize", getBgWidth);
      };
    },
    { scope: container, dependencies: [carouselContainer.current] }
  );

  const CarouselTitleElement = isHome ? "h1" : "h2";

  return (
    <section
      ref={container}
      className={classNames(className, c.container, isHome && c.heroGradient)}
      style={{ position: "relative" }}
      id="carouselWrap"
    >
      {isHome && (
        <div className={c.gradientWrap}>
          <MeshGradient opacity={0.75} />
        </div>
      )}
      <div
        className={classNames(c.carouselWrapper, !isHome && c.landingWrapper)}
        ref={carouselContainer}
        style={cta && { "--hoverText": `'${cta.label}'` }}
      >
        <div
          data-transition={!isHome && "slide-enter-child"}
          className={classNames("slide-enter-child", c.text, isHome && c.homeText)}
          id="carouselText"
          ref={textRef}
        >
          {!isHome && (
            <Text variant="eyebrow" as="h3" className={c.eyebrow}>
              {carouselLabel}
            </Text>
          )}
          <Text variant="h1" as={CarouselTitleElement} className={c.headline}>
            {carouselHeading}
          </Text>
          <Text variant="p2" as="div" className={classNames(c.subheading, isHome && c.homeSubheading)}>
            {carouselDescription && renderRichText(carouselDescription)}
          </Text>
          {cta && <ContentfulCta className={c.playVideoLink} {...cta} />}
          {isHome && (
            <div className={c.heroCta}>
              <VideoModal label={"Watch a video tour"} id={"carousel-modal"} />
            </div>
          )}
        </div>
        <Carousel
          className={classNames("home-carousel", c.carousel, isHome && c.homeCarousel)}
          config={carouselConfig}
          delay={5500}
        >
          <Carousel.Slider className={c.slider}>
            {isMobile && mobileSlides ? (
              mobileSlides.map(
                (slide, index) =>
                  index <= mobileSlides.length && (
                    <Carousel.Slide key={slide.id} className={c.slide} slideNumber={index}>
                      <figure>
                        <GatsbyImage image={getImage(slide)} alt="" loading="lazy" />
                      </figure>
                    </Carousel.Slide>
                  )
              )
            ) : desktopSlides ? (
              desktopSlides.map(
                (slide, index) =>
                  index <= desktopSlides.length && (
                    <Carousel.Slide key={slide.id} className={c.slide} slideNumber={index}>
                      <figure>
                        <GatsbyImage image={getImage(slide)} alt="" loading="lazy" />
                      </figure>
                    </Carousel.Slide>
                  )
              )
            ) : (
              <></>
            )}
          </Carousel.Slider>
          <Carousel.Controls
            isHome={isHome}
            controlText={imageLabels}
            className={classNames(c.controls, "carouselControls", isHome && c.buttons)}
            style="line"
            theme="dark"
          />
          {isHome && (
            <Carousel.Controls
              isHome={false}
              controlText={imageLabels}
              className={classNames(c.controls, "carouselControls", isHome && c.mobileControls)}
              style="line"
              theme="dark"
            />
          )}

          {imageLabels &&
            desktopSlides &&
            imageLabels.length === desktopSlides.length && ( // Let's only show these if there are enough labels to match slides
              <Carousel.Arrow
                mobile={isMobile}
                arrowText={imageLabels}
                className={classNames(c.arrow, "fade-enter", isHome && c.homeArrow)}
              />
            )}
        </Carousel>
      </div>
    </section>
  );
};

export { CarouselLarge };
